import React, { useEffect, useMemo, useState } from 'react'
import './card-details.styles.scss'
import Metatags from 'react-meta-tags'
import { loadStripe } from '@stripe/stripe-js'
import { useQueryParams } from '../../hooks/use-query-params.hook'
import {
  requestOtp,
  useCardDetailsToGetMerchantConfig,
  verifyOtp,
} from './card-details.hook'
import { Elements } from '@stripe/react-stripe-js'
import CardDetails from './components/card-details.component'
import { Col, Container, Row } from 'reactstrap'
import CardDetailsVerification from './components/card-details-verification.component'
import { isCompleteOtp } from './card-details-otp.helper'
import CardDetailsVerificationWeb from './components/card-details-verification-web.component'

// Number of seconds the revealed card details stay on screen in web mode before
// the modal auto-closes for security.
const WEB_REVEAL_SECONDS = 10

const CardDetailsPage = () => {
  const [isIOS, setIsIOS] = useState(false)
  const [cardDetailsComponent, setCardDetailsComponent] = useState(() => <></>)
  const [hasVerifiedEmail, setHasVerifiedEmail] = useState(false)
  const [otpRequested, setOtpRequested] = useState(false)
  const [loadedData, setLoadedData] = useState(false)
  const [otp, setOtp] = useState('')
  const [otpError, setOtpError] = useState('')
  const [secondsLeft, setSecondsLeft] = useState(WEB_REVEAL_SECONDS)
  const [detailsHidden, setDetailsHidden] = useState(false)
  const [showClosePrompt, setShowClosePrompt] = useState(false)

  const query = useQueryParams()
  const merchantId = useMemo(() => query.get('merchantId'), [query])
  const cardId = useMemo(() => query.get('cardId'), [query])
  const key = useMemo(() => query.get('key'), [query])
  const isWeb = useMemo(() => query.get('web') === 'true', [query])
  const { data, isLoading, isError } = useCardDetailsToGetMerchantConfig({
    merchantId,
    key,
  })
  const title = 'Lopay Card Details'

  const updateHasVerifiedEmail = (value: boolean) => {
    setHasVerifiedEmail(value)
  }

  const onSubmitOtp = async () => {
    if (isCompleteOtp(otp) && merchantId && key) {
      setOtpError('')
      try {
        await verifyOtp(otp, merchantId, key)
        updateHasVerifiedEmail(true)
      } catch (error) {
        setOtpError('Incorrect code')
      }
    }
  }

  const updateOtp = (value: string) => {
    setOtp(value)
  }

  const handleClose = () => {
    const base = (import.meta.env.VITE_BRANCH_BASE_URL || '').replace(/\/$/, '')
    window.location.href = `${base}/card_issuing`
  }

  // In web mode the page is a separate window/tab. Try to close it
  // programmatically (only works when the portal opened it via window.open);
  // if the browser blocks that, fall back to asking the user to close it.
  const handleWebClose = () => {
    window.close()
    window.setTimeout(() => setShowClosePrompt(true), 300)
  }

  useEffect(() => {
    const userAgent = window.navigator.userAgent
    setIsIOS(/iPhone|iPad|iPod/.test(userAgent))
  }, [])

  useEffect(() => {
    if (isError) {
      setCardDetailsComponent(
        <p>Something went wrong. Please contact Lopay support for help.</p>,
      )
    } else if (isLoading) {
      setCardDetailsComponent(<p>Loading...</p>)
    } else if (data) {
      if (merchantId && cardId && key) {
        setLoadedData(true)
        if (!hasVerifiedEmail) {
          if (!otpRequested) {
            requestOtp(merchantId, key)
            setOtpRequested(true)
          }
          setCardDetailsComponent(
            isWeb ? (
              <CardDetailsVerificationWeb
                updateOtp={updateOtp}
                otpError={otpError}
              ></CardDetailsVerificationWeb>
            ) : (
              <CardDetailsVerification
                updateOtp={updateOtp}
                otpError={otpError}
              ></CardDetailsVerification>
            ),
          )
        } else {
          const stripePromise = loadStripe(data.stripePublicKey, {
            stripeAccount: data.stripeAccountId,
          })
          setCardDetailsComponent(
            <Elements stripe={stripePromise}>
              <CardDetails
                merchantId={merchantId}
                cardId={cardId}
                apiKey={key}
              />
            </Elements>,
          )
        }
      }
    }
  }, [
    hasVerifiedEmail,
    isError,
    isLoading,
    data,
    cardId,
    merchantId,
    key,
    setCardDetailsComponent,
    otpRequested,
    otpError,
    isWeb,
  ])

  // In web mode the revealed details auto-hide after a short countdown. When it
  // expires we hide the details (rather than close the popup) and offer a button
  // to show them again without re-authenticating.
  useEffect(() => {
    if (!isWeb || !hasVerifiedEmail || detailsHidden) {
      return
    }
    setSecondsLeft(WEB_REVEAL_SECONDS)
    const intervalId = setInterval(() => {
      setSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId)
          setDetailsHidden(true)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(intervalId)
  }, [isWeb, hasVerifiedEmail, detailsHidden])

  const handleShowDetailsAgain = () => {
    setDetailsHidden(false)
  }

  const otpComplete = otp.replace('-', '').length >= 6

  if (isWeb) {
    return (
      <React.Fragment>
        <Metatags>{title}</Metatags>
        <div className="card-details card-details--web">
          <div className="card-details-modal-backdrop">
            <div className="card-details-modal">
              {showClosePrompt ? (
                <>
                  <div className="card-details-modal-lock">
                    <i className="fas fa-lock"></i>
                  </div>
                  <p className="card-details-modal-security">
                    You can now close this window.
                  </p>
                </>
              ) : !hasVerifiedEmail ? (
                <>
                  <div className="card-details-modal-header">
                    <h1 className="card-details-modal-title">
                      Verify your account
                    </h1>
                    <button
                      type="button"
                      className="card-details-modal-close"
                      onClick={handleWebClose}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                  <p className="card-details-modal-subtitle">
                    We have sent you an email with a code, please enter this
                    code below
                  </p>
                  {cardDetailsComponent}
                  {loadedData && (
                    <button
                      className="card-details-modal-button"
                      disabled={!otpComplete}
                      onClick={onSubmitOtp}
                    >
                      Continue
                    </button>
                  )}
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="card-details-modal-close card-details-modal-close--corner"
                    onClick={handleWebClose}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                  <div className="card-details-modal-lock">
                    <i className="fas fa-lock"></i>
                  </div>
                  <div
                    style={{
                      display: detailsHidden ? 'none' : 'block',
                    }}
                  >
                    {cardDetailsComponent}
                  </div>
                  {detailsHidden ? (
                    <>
                      <p className="card-details-modal-security">
                        Your card details have been hidden for security.
                      </p>
                      <button
                        className="card-details-modal-button"
                        onClick={handleShowDetailsAgain}
                      >
                        Show details
                      </button>
                    </>
                  ) : (
                    <>
                      <p className="card-details-modal-security">
                        For security reasons this popup will be removed in{' '}
                        {secondsLeft} secs
                      </p>
                      <button
                        className="card-details-modal-button"
                        onClick={handleWebClose}
                      >
                        OK
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </React.Fragment>
    )
  }

  return (
    <React.Fragment>
      <Metatags>{title}</Metatags>
      <div className="card-details">
        <Container fluid className="p-0">
          <Row className="g-0">
            <Col lg={6} md={6} className="col-xxl-3">
              {!isIOS && (
                <div
                  className="card-details-close-button"
                  onClick={handleClose}
                >
                  <i className="fas fa-times fa-lg"></i>
                </div>
              )}
              <div
                className="w-100 card-details-wrapper"
                style={!isIOS ? { padding: '1.5rem 1rem' } : {}}
              >
                <div>
                  <h1 className="card-details-title">
                    {hasVerifiedEmail ? 'Card information' : 'Verify'}
                  </h1>
                  {cardDetailsComponent}
                </div>
                {loadedData && cardDetailsComponent && !hasVerifiedEmail && (
                  <div className="card-details-button-wrapper">
                    <button
                      className="card-details-button"
                      disabled={!isCompleteOtp(otp)}
                      onClick={onSubmitOtp}
                    >
                      Continue
                    </button>
                    {!isIOS && <span onClick={handleClose}>Cancel</span>}
                  </div>
                )}
                {!isIOS &&
                  loadedData &&
                  cardDetailsComponent &&
                  hasVerifiedEmail && (
                    <button
                      className="card-details-button"
                      onClick={handleClose}
                    >
                      Done
                    </button>
                  )}
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default CardDetailsPage
